<div class="page">
  <div class="page-head">
    <div>
      <h2>{{ pageTitle() || 'Global Master' }}</h2>
      <p>Manage data types and the values that belong to each one.</p>
    </div>
  </div>

  <div class="md-grid">
    <!-- ===================== LEFT — DATA TYPES ===================== -->
    <div class="card">
      <div class="panel-head">
        <h3>Data Types</h3>
        <p-button label="Add" icon="pi pi-plus" size="small" (onClick)="openAddType()" />
      </div>

      <p-table [value]="types()" [loading]="loadingTypes()" dataKey="id" [rows]="10"
        [paginator]="types().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
        <ng-template pTemplate="header">
          <tr>
            <th>Name</th>
            <th style="width: 80px" class="ta-c">Values</th>
            <th style="width: 90px">Active</th>
            <th style="width: 110px" class="ta-r">Actions</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-t>
          <tr class="click-row" [class.row-on]="isSelected(t)" (click)="selectType(t)">
            <td>
              <div class="name">{{ t.dataTypeName }}</div>
              @if (t.displayName) { <div class="sub">{{ t.displayName }}</div> }
            </td>
            <td class="ta-c"><p-tag [value]="t.valueCount" severity="info" /></td>
            <td>
              <span class="active-toggle" (click)="$event.stopPropagation(); toggleType(t)" pTooltip="Click to toggle">
                <p-tag [value]="t.isActive ? 'Active' : 'Inactive'" [severity]="t.isActive ? 'success' : 'secondary'" />
              </span>
            </td>
            <td class="ta-r actions" (click)="$event.stopPropagation()">
              <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
                pTooltip="Edit" (onClick)="openEditType(t)" />
              <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
                [disabled]="t.valueCount > 0"
                [pTooltip]="t.valueCount > 0 ? 'Disable instead — values still belong to this type' : 'Delete'"
                (onClick)="removeType(t)" />
            </td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="4">
              <div class="state"><i class="pi pi-database"></i><p>No data types yet. Add one to get started.</p></div>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>

    <!-- ===================== RIGHT — VALUES ===================== -->
    <div class="card">
      <div class="panel-head">
        <h3>
          Values
          @if (selectedName()) { <span class="for-type">— {{ selectedName() }}</span> }
        </h3>
        <p-button label="Add value" icon="pi pi-plus" size="small" [disabled]="!selected()" (onClick)="openAddValue()" />
      </div>

      @if (!selected()) {
        <div class="state"><i class="pi pi-arrow-left"></i><p>Select a data type to see its values.</p></div>
      } @else {
        <p-table [value]="values()" [loading]="loadingValues()" dataKey="id" [rows]="10"
          [paginator]="values().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first}–{last} of {totalRecords}">
          <ng-template pTemplate="header">
            <tr>
              <th>Value</th>
              <th style="width: 80px">Order</th>
              <th style="width: 90px">Active</th>
              <th style="width: 110px" class="ta-r">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-r>
            <tr>
              <td class="name">{{ r.dataValue }}</td>
              <td class="mono">{{ r.displayOrder ?? 0 }}</td>
              <td>
                <span class="active-toggle" (click)="toggleValue(r)" pTooltip="Click to toggle">
                  <p-tag [value]="r.isActive ? 'Active' : 'Inactive'" [severity]="r.isActive ? 'success' : 'secondary'" />
                </span>
              </td>
              <td class="ta-r actions">
                <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
                  pTooltip="Edit" (onClick)="openEditValue(r)" />
                <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
                  pTooltip="Delete" (onClick)="removeValue(r)" />
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4">
                <div class="state"><i class="pi pi-inbox"></i>
                  <p>No values under "{{ selectedName() }}" yet.</p>
                  <p-button label="Add value" icon="pi pi-plus" size="small" [outlined]="true" (onClick)="openAddValue()" />
                </div>
              </td>
            </tr>
          </ng-template>
        </p-table>
      }
    </div>
  </div>
</div>

<!-- ===================== DATA TYPE DIALOG ===================== -->
<p-dialog [visible]="showTypeModal()" (visibleChange)="showTypeModal.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [closable]="true" [dismissableMask]="false"
  [header]="editingType() ? 'Edit Data Type' : 'New Data Type'" styleClass="menu-dialog"
  [style]="{ width: '520px' }" [breakpoints]="{ '520px': '94vw' }">
  <form [formGroup]="typeForm" class="menu-form">
    <div class="field">
      <label>Data Type Name <span class="req">*</span></label>
      <input pInputText type="text" formControlName="dataTypeName" placeholder="Gender" autocomplete="off">
      @if (typeForm.get('dataTypeName')?.touched && typeForm.get('dataTypeName')?.invalid) {
        <small class="err">Data type name is required</small>
      } @else if (editingType()) {
        <small class="hint-muted"><i class="pi pi-info-circle"></i> Renaming cascades to all values under this type.</small>
      } @else {
        <small class="hint-muted">The key the values reference.</small>
      }
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Display Name</label>
        <input pInputText type="text" formControlName="displayName" placeholder="Gender">
      </div>
      <div class="field">
        <label>Sort Order</label>
        <p-inputNumber formControlName="sortOrder" [min]="0" [showButtons]="true" [useGrouping]="false" />
      </div>
    </div>

    <div class="field">
      <label>Description</label>
      <textarea formControlName="description" rows="2" class="form-textarea" placeholder="Optional"></textarea>
    </div>

    <label class="switch-row">
      <p-toggleSwitch formControlName="isActive" />
      <span>Active</span>
    </label>

    <div class="modal-actions">
      <p-button label="Cancel" [outlined]="true" (onClick)="closeTypeModal()" />
      <p-button label="Save" (onClick)="saveType()" [loading]="savingType()" />
    </div>
  </form>
</p-dialog>

<!-- ===================== VALUE DIALOG ===================== -->
<p-dialog [visible]="showValueModal()" (visibleChange)="showValueModal.set($event)" [modal]="true" [draggable]="true"
  [resizable]="false" [closable]="true" [dismissableMask]="false"
  [header]="editingValue() ? 'Edit Value' : 'New Value'" styleClass="menu-dialog"
  [style]="{ width: '520px' }" [breakpoints]="{ '520px': '94vw' }">
  <form [formGroup]="valueForm" class="menu-form">
    <div class="field">
      <label>Data Type <span class="req">*</span></label>
      <p-select formControlName="dataType" [options]="activeTypes()" optionLabel="label" optionValue="value"
        appendTo="body" placeholder="Data type" />
      <small class="hint-muted">Locked to the selected data type.</small>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Value <span class="req">*</span></label>
        <input pInputText type="text" formControlName="dataValue" placeholder="Male" autocomplete="off">
        @if (valueForm.get('dataValue')?.touched && valueForm.get('dataValue')?.invalid) {
          <small class="err">Value is required</small>
        } @else {
          <small class="hint-muted">Unique within the type.</small>
        }
      </div>
      <div class="field">
        <label>Display Order</label>
        <p-inputNumber formControlName="displayOrder" [min]="0" [showButtons]="true" [useGrouping]="false" />
      </div>
    </div>

    <label class="switch-row">
      <p-toggleSwitch formControlName="isActive" />
      <span>Active</span>
    </label>

    <div class="modal-actions">
      <p-button label="Cancel" [outlined]="true" (onClick)="closeValueModal()" />
      <p-button label="Save" (onClick)="saveValue()" [loading]="savingValue()" />
    </div>
  </form>
</p-dialog>
